import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCartContext } from "./CartContext";
import { useWarehouseContext } from "./WarehouseContext";
import { establishSSOSession } from "../../services/backend/authService";
import { StorageKeys, session } from "../../utils/storage";
import logger from "../../shared/utils/logger";

const AuthContext = createContext();
export const useAuthContext = () => useContext(AuthContext);

const SESSION_KEYS = [
  StorageKeys.APP_USER,
  StorageKeys.APP_PERFIL,
  StorageKeys.APP_CANAL,
  StorageKeys.APP_ROL_CREDITO,
  StorageKeys.AUTH_USERNAME,
  StorageKeys.AUTH_PASSWORD,
  StorageKeys.AUTH_ID,
];

const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const { loadCotizacionFromBackend } = useCartContext();
  const { selectedAlmacen } = useWarehouseContext();

  const [appUser, setAppUser] = useState(() => session.get(StorageKeys.APP_USER));
  const [appPerfil, setAppPerfil] = useState(() => session.get(StorageKeys.APP_PERFIL));
  const [appCanal, setAppCanal] = useState(() => session.get(StorageKeys.APP_CANAL));
  const [appRolCredito, setAppRolCredito] = useState(() => session.get(StorageKeys.APP_ROL_CREDITO));

  const startSession = useCallback(async ({ user = "", perfil = "", canal = "", rolCredito = "" }) => {
    const safeCanal = String(canal).trim().toUpperCase();
    const safeRolCredito = String(rolCredito).trim().toUpperCase();
    session.set(StorageKeys.APP_USER, user);
    session.set(StorageKeys.APP_PERFIL, perfil);
    session.set(StorageKeys.APP_CANAL, safeCanal);
    session.set(StorageKeys.APP_ROL_CREDITO, safeRolCredito);
    setAppUser(user);
    setAppPerfil(perfil);
    setAppCanal(safeCanal);
    setAppRolCredito(safeRolCredito);
    await loadCotizacionFromBackend(selectedAlmacen);
  }, [loadCotizacionFromBackend, selectedAlmacen]);

  const endSession = useCallback(() => {
    SESSION_KEYS.forEach((key) => session.remove(key));
    setAppUser("");
    setAppPerfil("");
    setAppCanal("");
    setAppRolCredito("");
    navigate("/");
  }, [navigate]);

  const ssoValidatedRef = useRef(false);

  useEffect(() => {
    if (ssoValidatedRef.current || appUser) return;
    ssoValidatedRef.current = true;
    establishSSOSession()
      .then(async (context) => {
        await startSession({
          user: context.username,
          perfil: context.appPerfil,
          canal: context.appCanal,
          rolCredito: context.rolCredito,
        });
      })
      .catch((error) => {
        logger.warn("No fue posible establecer sesión SSO, se continúa como invitado.", error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(() => ({
    appUser,
    appPerfil,
    appCanal,
    appRolCredito,
    isAuthenticated: Boolean(appUser),
    startSession,
    endSession,
  }), [appUser, appPerfil, appCanal, appRolCredito, startSession, endSession]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
